import { useEffect } from "react";
import { useParams } from "react-router";
import { styled } from "styled-components";

import { bookmarksApiSlice } from "api/bookmarksApi.slice";
import {
  ExpandableText,
  JoinUsUpgradeBanner,
  MasterDetailContentWrapper,
  SectionTitleUnderlined,
  UIActionButton,
  UIButtonsRow,
} from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import type { TopicBackLabelData } from "context/session/sessionTypes";
import { getUserId } from "domains/authentication/selectors";
import { KnowledgeDataDetail } from "domains/knowledgeData/components/KnowledgeDataDetail";
import { getSiteLanguage } from "domains/language/selectors";
import { fetchTopicsForPinnedKnowledge } from "domains/pinAddReject/actions";
import { KnowledgeDetailPinButton } from "domains/pinAddReject/components/KnowledgeDetailPinButton";
import { KnowledgeRejectButton } from "domains/pinAddReject/components/KnowledgeRejectButton";
import { checkIsKnowledgeStored } from "domains/pinAddReject/selectors";
import { ArticleDetail } from "domains/publication/components/ArticleDetail";
import { useTranslationLink } from "domains/publication/useTranslationLink";
import { shareLinkAction } from "domains/share/actions";
import { KnowledgeSignalSection } from "domains/signals/components/KnowledgeSignalSection";
import { TopicSlider } from "domains/topic/components/TopicSlider";
import { VideoDetail } from "domains/videos/components/VideoDetail";
import { fetchKnowledgeById } from "entities/knowledge/actions";
import type { Knowledge } from "entities/knowledge/types";
import { KnowledgeType } from "entities/knowledge/types";
import { getCanAccessMonitor, getCanDisplayBookmarks } from "entities/permissions/selectors";

const StyledTitle = styled.h2`
  font-size: 2rem;
  margin: 0.6rem 0;
`;

const StyledContent = styled.div`
  height: 100%;
  margin: 1rem 0;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

const StyledSpaceBetweenRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledButtons = styled(UIButtonsRow)`
  margin-top: 2rem;
`;

const StyledDescription = styled(ExpandableText)`
  p {
    font-size: 1.1rem;
    line-height: 1.75rem;
    white-space: pre-line;
  }
`;

const StyledContentSeparationSpace = styled.div`
  height: 2rem;
`;

type KnowledgeDetailProps = { id?: string; knowledge?: Knowledge; shareLink?: string; topicBack: TopicBackLabelData };

export const KnowledgeDetail = ({ id, knowledge, shareLink, topicBack }: KnowledgeDetailProps) => {
  const dispatch = useDispatch();
  const { topicId } = useParams();
  const hasAccessToMonitor = useSelector(getCanAccessMonitor);
  const hasAccessToBookmarks = useSelector(getCanDisplayBookmarks);
  const userId = useSelector(getUserId);
  const language = useSelector(getSiteLanguage);
  const track = useTrack();
  const translate = useTranslate();
  const { translationUrl, displayTranslationLink, translationLinkLabel, onTranslationLinkClick } = useTranslationLink({
    id: knowledge?.id,
    title: knowledge?.title,
    language: knowledge?.language ?? undefined,
    link: knowledge?.link,
  });

  const isKnowledgeStored = useSelector(checkIsKnowledgeStored(knowledge?.id ?? ""));

  const [addKnowledgeToBookmarks] = bookmarksApiSlice.endpoints.addKnowledgeToBookmarks.useMutation();
  const [removeKnowledgeFromBookmarks] = bookmarksApiSlice.endpoints.removeKnowledgeFromBookmarks.useMutation();

  useEffect(() => {
    if (isKnowledgeStored) return;

    if (!knowledge?.id) return;

    dispatch(fetchTopicsForPinnedKnowledge({ knowledgeId: knowledge.id }));
  }, [dispatch, isKnowledgeStored, knowledge?.id]);

  useEffect(() => {
    if (id) {
      dispatch(fetchKnowledgeById({ id }));
    }
  }, [dispatch, id, language, userId]);

  if (!knowledge) {
    return null;
  }

  const { title, summary, link, isBookmarkPending, is_bookmarked, topics = [], signals } = knowledge;

  const onBookmarkHandle = async () => {
    if (knowledge.is_bookmarked) {
      await removeKnowledgeFromBookmarks({ id: knowledge.id });
    } else {
      await addKnowledgeToBookmarks({ id: knowledge.id });
    }
  };

  const trackOpenLink = () => track({ entityId: id, entityName: title, entityType: "Knowledge", name: "Open Link" });

  const onShareClick = () =>
    dispatch(
      shareLinkAction({
        id: id ?? "",
        name: title,
        link: shareLink ?? "",
        type: "Knowledge",
      }),
    );

  let topicsSlider = null;
  if (!hasAccessToMonitor) {
    topicsSlider = (
      <MasterDetailContentWrapper>
        <JoinUsUpgradeBanner trackPage="Knowledge detail" />
      </MasterDetailContentWrapper>
    );
  } else if (topics.length > 0) {
    topicsSlider = (
      <>
        <MasterDetailContentWrapper>
          <SectionTitleUnderlined>{translate("app.topic.related_topics")}</SectionTitleUnderlined>
          <TopicSlider
            hideFollowAction
            numberOfItems={{ small: 1, medium: 3, large: 2, extraLarge: 3, enormous: 4 }}
            topicBack={topicBack}
            topics={topics}
          />
        </MasterDetailContentWrapper>
      </>
    );
  }

  let mainContent = undefined;
  if (knowledge.knowledgeType === KnowledgeType.video) {
    mainContent = (
      <VideoDetail
        title={knowledge.title}
        videoUrl={knowledge.embedded_media?.url ?? knowledge.attachment?.url}
        html={knowledge.embedded_media?.html ?? undefined}
        videoHeight={knowledge.embedded_media?.height}
        videoWidth={knowledge.embedded_media?.width}
        source={knowledge.source}
        timestamp={knowledge.time}
      />
    );
  } else if (knowledge.interactive_link) {
    mainContent = (
      <KnowledgeDataDetail
        title={knowledge.title}
        interactiveLink={knowledge.interactive_link}
        source={knowledge.source}
        timestamp={knowledge.time}
      />
    );
  } else {
    mainContent = (
      <ArticleDetail
        title={knowledge.title}
        imageUrl={knowledge.images?.medium}
        source={knowledge.source}
        timestamp={knowledge.time}
      />
    );
  }

  return (
    <>
      <MasterDetailContentWrapper>
        {mainContent}
        <StyledContent>
          <StyledTitle>{title}</StyledTitle>
          <StyledDescription text={summary} />
          <StyledSpaceBetweenRow>
            <StyledButtons>
              <UIActionButton type="open" href={link} onClick={trackOpenLink} />
              {shareLink && id && <UIActionButton type="share" onClick={onShareClick} />}
              {hasAccessToBookmarks && (
                <UIActionButton
                  type="bookmark"
                  isActive={is_bookmarked}
                  isLoading={isBookmarkPending}
                  onClick={onBookmarkHandle}
                />
              )}
              {displayTranslationLink && (
                <UIActionButton
                  type="open"
                  href={translationUrl}
                  onClick={onTranslationLinkClick}
                  label={translationLinkLabel}
                />
              )}
            </StyledButtons>
            <StyledButtons>
              {topicId && (
                <KnowledgeRejectButton
                  knowledgeId={knowledge.id}
                  knowledgeType={knowledge.knowledgeType}
                  onSuccessPath={`/topics/${topicId}${getPathSegment(knowledge.knowledgeType)}`}
                  topicId={topicId}
                />
              )}
              {topicId && (
                <KnowledgeDetailPinButton
                  topicId={topicId}
                  knowledgeId={knowledge.id}
                  isPinned={!!knowledge.is_pinned}
                />
              )}
            </StyledButtons>
          </StyledSpaceBetweenRow>
        </StyledContent>
        <KnowledgeSignalSection signals={signals} />
      </MasterDetailContentWrapper>
      <StyledContentSeparationSpace />
      {topicsSlider}
    </>
  );
};

const getPathSegment = (knowledgeType: KnowledgeType) => {
  switch (knowledgeType) {
    case KnowledgeType.video:
      return "/videos";
    case KnowledgeType.publication:
      return "/publications";
    case KnowledgeType.data:
      return "/interactive-data";
    default:
      return "";
  }
};
